import { supabase } from "./supabase";
import { blockNoun, NEUTRAL_BLOCK_NOUN, type BlockNoun } from "./blockNoun";
import type {
  ActionChange,
  ActionFindingLink,
  ActionRow,
  ActionStatus,
  AssessedEntity,
  BlockResponse,
  CriterionResponse,
  Engagement,
  EvaluationRow,
  EvidenceKind,
  EvidenceReference,
  FindingKernelOrigin,
  FindingRow,
  FindingSeverity,
  FindingStatus,
  InstrumentBlock,
  InstrumentCriterion,
  InstrumentTrigger,
  LifecycleStatus,
  MemberDirectoryEntry,
  MembershipRole,
  NewAssessedEntity,
  NewEngagement,
  NewRun,
  Organisation,
  OrganisationMembership,
  PublishedVersion,
  ResponseEvidenceLink,
  Run,
  RunAssignment,
  RunBaseline,
  RunState,
} from "./types";
import type { AssignedRun } from "./respondent";
import {
  selectEligibleEvaluation,
  selectLatestEvaluation,
} from "./evaluationEligibility";

// Every function either returns data or throws the PostgREST error message.
// RLS denials surface as empty results for reads and thrown errors for
// writes - callers render both, per the §11 exception-state obligation.

function unwrap<T>(data: T | null, error: { message: string } | null): T {
  if (error) throw new Error(error.message);
  if (data === null) throw new Error("no data returned");
  return data;
}

/** The tenants the signed-in user belongs to. RLS scopes this to memberships. */
export async function listOrganisations(): Promise<Organisation[]> {
  const { data, error } = await supabase
    .from("organisations")
    .select("*")
    .order("name");
  return unwrap(data, error);
}

/**
 * The caller's OWN membership rows - the console derives authority from these.
 *
 * The explicit user filter is load-bearing: RLS makes the whole tenant roster
 * readable to every member (0001 §2, so the console can show it), so an
 * unfiltered select returns colleagues' rows too - and a role lookup that
 * takes "the first row for this org" takes someone else's authority. Found
 * live: a viewer was shown the create-run form because the owner's row came
 * back first. The database refused the write either way; the console must
 * not offer it.
 */
export async function listMyMemberships(): Promise<OrganisationMembership[]> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError) throw new Error(userError.message);
  const uid = userData.user?.id;
  if (!uid) throw new Error("no authenticated user");
  const { data, error } = await supabase
    .from("organisation_memberships")
    .select("*")
    .eq("user_id", uid);
  return unwrap(data, error);
}

/**
 * The current actor's authority in ONE organisation - the dedicated query the
 * session 2 review requires: exactly zero or one row, fail closed, never a
 * fallback to the roster. Returns null when the caller has no membership,
 * and every caller must treat null as "no authority", not as an error to
 * paper over. The UI check remains a usability control; the database stays
 * the boundary.
 */
export async function myAuthority(orgId: string): Promise<MembershipRole | null> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError) throw new Error(userError.message);
  const uid = userData.user?.id;
  if (!uid) throw new Error("no authenticated user");
  const { data, error } = await supabase
    .from("organisation_memberships")
    .select("role")
    .eq("organisation_id", orgId)
    .eq("user_id", uid)
    .maybeSingle();               // >1 row is impossible (PK) and would throw
  if (error) throw new Error(error.message);
  return (data?.role as MembershipRole | undefined) ?? null;
}

export async function listEntities(orgId: string): Promise<AssessedEntity[]> {
  const { data, error } = await supabase
    .from("assessed_entities")
    .select("*")
    .eq("organisation_id", orgId)
    .order("created_at", { ascending: false });
  return unwrap(data, error);
}

export async function createEntity(input: NewAssessedEntity): Promise<AssessedEntity> {
  const { data, error } = await supabase
    .from("assessed_entities")
    .insert(input)
    .select()
    .single();
  return unwrap(data, error);
}

export async function listEngagements(orgId: string): Promise<Engagement[]> {
  const { data, error } = await supabase
    .from("engagements")
    .select("*")
    .eq("organisation_id", orgId)
    .order("created_at", { ascending: false });
  return unwrap(data, error);
}

export async function createEngagement(input: NewEngagement): Promise<Engagement> {
  const { data, error } = await supabase
    .from("engagements")
    .insert(input)
    .select()
    .single();
  return unwrap(data, error);
}

/**
 * Lifecycle transition - owner-only, enforced by app.protect_tenant_row().
 * The payload is `{ status }` alone; attribution is derived server-side and
 * supplying it is rejected as forgery.
 */
export async function setLifecycle(
  table: "assessed_entities" | "engagements",
  id: string,
  status: LifecycleStatus,
): Promise<void> {
  const { error } = await supabase.from(table).update({ status }).eq("id", id);
  if (error) throw new Error(error.message);
}

// ---- Runs (session 2) --------------------------------------------------

export async function getEngagement(id: string): Promise<Engagement | null> {
  const { data, error } = await supabase
    .from("engagements")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}

export async function listRuns(engagementId: string): Promise<Run[]> {
  const { data, error } = await supabase
    .from("runs")
    .select("*")
    .eq("engagement_id", engagementId)
    .order("created_at", { ascending: false });
  return unwrap(data, error);
}

export async function getRun(id: string): Promise<Run | null> {
  const { data, error } = await supabase
    .from("runs")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}

/**
 * The versions a run may bind to. RLS already hides drafts; the explicit
 * status filter additionally excludes superseded versions, which stay
 * readable so old runs remain explicable (ADR-05) but are not offered for
 * new ones.
 */
export async function listPublishedVersions(): Promise<PublishedVersion[]> {
  const { data, error } = await supabase
    .from("instrument_versions")
    .select("id, version, definition_hash, instruments (slug, title)")
    .eq("status", "published")
    .order("version");
  return unwrap(data as PublishedVersion[] | null, error);
}

export async function createRun(input: NewRun): Promise<Run> {
  const { data, error } = await supabase
    .from("runs")
    .insert(input)
    .select()
    .single();
  return unwrap(data, error);
}

/** Every run of one organisation, for the invitations list (RLS: members only). */
export async function listRunsForOrganisation(orgId: string): Promise<Run[]> {
  const { data, error } = await supabase
    .from("runs")
    .select("*")
    .eq("organisation_id", orgId)
    .order("created_at", { ascending: false });
  return unwrap(data, error);
}

/** Every assignment of one organisation's runs. */
export async function listAssignmentsForOrganisation(orgId: string): Promise<{ run_id: string; assessor_id: string }[]> {
  const { data, error } = await supabase
    .from("run_assignments")
    .select("run_id, assessor_id")
    .eq("organisation_id", orgId);
  return unwrap(data as { run_id: string; assessor_id: string }[] | null, error);
}

/** How many criterion answers each run holds — the invitations list's "Answering". */
export async function countAnswersByRun(runIds: readonly string[]): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  if (runIds.length === 0) return counts;
  const { data, error } = await supabase
    .from("run_criterion_responses")
    .select("run_id")
    .in("run_id", [...runIds]);
  const rows = unwrap(data as { run_id: string }[] | null, error);
  for (const r of rows) counts.set(r.run_id, (counts.get(r.run_id) ?? 0) + 1);
  return counts;
}

/**
 * The invite screen's mail (DESIGN_SLICE_INVITE_SCREEN 281026 V1.0): Auth sends
 * the Magic Link template to an EXISTING identity; the link exchanges on
 * /auth/accept as type=magiclink and signs the respondent in. No identity is
 * created here (shouldCreateUser false): membership stays with the governed
 * path. The address is the roster's, typed by the administrator.
 */
/**
 * One magic-link mail. `createUser` is the invite screen's first press for a
 * person not yet on the roster (DESIGN_SLICE_INVITE_COMMAND 291026 V1.0):
 * Supabase Auth creates the identity, and 0023's trigger binds it to the
 * invitation the session recorded — or refuses the identity when none is
 * recorded, which arrives here as an error. For that new identity Auth sends
 * the Confirm sign up template, not Magic Link: supabase/templates/signup.html
 * links /auth/accept with type=signup, which signs the person in the same way.
 */
/**
 * Records the invitation of a new person into an organisation the caller
 * administers (0024): the database keeps a salted digest of the address, the
 * role, and the caller as operator. The identity and the membership follow
 * from the magic-link mail (0023's claim). Returns the invitation id.
 */
export async function inviteMember(orgId: string, email: string, role: "assessor" | "viewer"): Promise<string> {
  const { data, error } = await supabase.rpc("assessment_invite_member", {
    p_org: orgId,
    p_email: email,
    p_role: role,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

/**
 * The invitation's text, when given, is the new identity's sign-up data
 * (user metadata), which the Confirm sign up template prints. Supabase Auth
 * stores it only when it creates the identity; a known person's call ignores it.
 */
export async function sendSignInLink(email: string, createUser = false, invitationText?: string): Promise<void> {
  const options = invitationText
    ? { shouldCreateUser: createUser, data: { invitation_text: invitationText } }
    : { shouldCreateUser: createUser };
  const { error } = await supabase.auth.signInWithOtp({ email, options });
  if (error) throw new Error(error.message);
}

export async function getAssignment(runId: string): Promise<RunAssignment | null> {
  const { data, error } = await supabase
    .from("run_assignments")
    .select("*")
    .eq("run_id", runId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}

/** Insert or reassign. Attribution derives server-side either way (0007 §2). */
export async function assignAssessor(
  runId: string,
  orgId: string,
  assessorId: string,
  existing: boolean,
): Promise<void> {
  const { error } = existing
    ? await supabase
        .from("run_assignments")
        .update({ assessor_id: assessorId })
        .eq("run_id", runId)
    : await supabase
        .from("run_assignments")
        .insert({ run_id: runId, organisation_id: orgId, assessor_id: assessorId });
  if (error) throw new Error(error.message);
}

/** The tenant roster with emails, for the assignment picker (0007 §3). */
export async function memberDirectory(orgId: string): Promise<MemberDirectoryEntry[]> {
  const { data, error } = await supabase.rpc("assessment_member_directory", {
    p_org: orgId,
  });
  return unwrap(data as MemberDirectoryEntry[] | null, error);
}

/**
 * The ONLY way a run changes state (0004 §5). The database validates the
 * transition, the caller's authority for it, and - for finalization - the
 * presence of a validated result.
 */
export async function transitionRun(runId: string, toState: RunState): Promise<RunState> {
  const { data, error } = await supabase.rpc("assessment_transition_run", {
    p_run_id: runId,
    p_to_state: toState,
  });
  if (error) throw new Error(error.message);
  return data as RunState;
}

// ---- Workspace (session 3) --------------------------------------------
// The snapshot queries filter by the RUN'S instrument_version_id, which is
// immutable after creation (0004 column grants) - that is what makes gates 1
// and 2 hold: a reload re-reads the pin, and nothing the client does can
// point it at a newer version.

export async function listInstrumentBlocks(versionId: string): Promise<InstrumentBlock[]> {
  const { data, error } = await supabase
    .from("instrument_blocks")
    .select("*")
    .eq("instrument_version_id", versionId)
    .order("sequence");
  return unwrap(data, error);
}

/** The block noun of a pinned version (E-F12): read from its blocks' declared
 *  kinds, never from its family. A label only, so a failed read gives the
 *  neutral noun rather than failing the screen that wanted a word. */
export async function blockNounForVersion(versionId: string): Promise<BlockNoun> {
  try {
    return blockNoun((await listInstrumentBlocks(versionId)).map((b) => b.kind));
  } catch {
    return NEUTRAL_BLOCK_NOUN;
  }
}

export async function listInstrumentCriteria(versionId: string): Promise<InstrumentCriterion[]> {
  const { data, error } = await supabase
    .from("instrument_criteria")
    .select("*")
    .eq("instrument_version_id", versionId)
    .order("sequence");
  return unwrap(data, error);
}

export async function listInstrumentTriggers(versionId: string): Promise<InstrumentTrigger[]> {
  const { data, error } = await supabase
    .from("instrument_triggers")
    .select("*")
    .eq("instrument_version_id", versionId);
  return unwrap(data, error);
}

export interface ScaleLevel {
  value: number;
  state: string;
  anchor: string;
}

/**
 * Tier-1 scoring anchors from the pinned definition's `scales` policy
 * section. They travel inside the definition hash (ADR-27), so what the
 * assessor reads is what the run is locked to.
 */
export async function listScaleAnchors(
  versionId: string,
): Promise<Map<string, ScaleLevel[]>> {
  const { data, error } = await supabase
    .from("instrument_policy")
    .select("body")
    .eq("instrument_version_id", versionId)
    .eq("section", "scales")
    .maybeSingle();
  if (error) throw new Error(error.message);
  const out = new Map<string, ScaleLevel[]>();
  const body = (data?.body ?? {}) as Record<string, { levels?: ScaleLevel[] }>;
  for (const [key, scale] of Object.entries(body)) {
    if (Array.isArray(scale.levels)) out.set(key, scale.levels);
  }
  return out;
}

export async function listBlockResponses(runId: string): Promise<BlockResponse[]> {
  const { data, error } = await supabase
    .from("run_block_responses")
    .select("*")
    .eq("run_id", runId);
  return unwrap(data, error);
}

export async function listCriterionResponses(runId: string): Promise<CriterionResponse[]> {
  const { data, error } = await supabase
    .from("run_criterion_responses")
    .select("*")
    .eq("run_id", runId);
  return unwrap(data, error);
}

/**
 * Response rows are created ON FIRST EDIT, never on open - opening a run
 * must not manufacture assessment data (session 2 decision, and gate S2).
 */
export async function createBlockResponse(
  orgId: string,
  runId: string,
  blockId: string,
): Promise<BlockResponse> {
  const { data, error } = await supabase
    .from("run_block_responses")
    .insert({ organisation_id: orgId, run_id: runId, block_id: blockId })
    .select()
    .single();
  return unwrap(data, error);
}

export async function updateBlockResponse(
  id: string,
  patch: Partial<
    Pick<
      BlockResponse,
      | "applicability"
      | "rationale"
      | "combined_into_block_id"
      | "combined_into_criterion_id"
      | "trigger_confirmed"
      | "trigger_rationale"
    >
  >,
): Promise<BlockResponse> {
  const { data, error } = await supabase
    .from("run_block_responses")
    .update(patch)
    .eq("id", id)
    .select()
    .single();
  return unwrap(data, error);
}

export async function createCriterionResponse(
  orgId: string,
  runId: string,
  blockResponseId: string,
  criterionKey: string,
): Promise<CriterionResponse> {
  const { data, error } = await supabase
    .from("run_criterion_responses")
    .insert({
      organisation_id: orgId,
      run_id: runId,
      block_response_id: blockResponseId,
      criterion_id: criterionKey,
    })
    .select()
    .single();
  return unwrap(data, error);
}

export async function updateCriterionResponse(
  id: string,
  patch: Partial<
    Pick<
      CriterionResponse,
      | "applicability"
      | "score"
      | "rationale"
      | "observation"
      | "combined_into_block_id"
      | "combined_into_criterion_id"
    >
  >,
): Promise<CriterionResponse> {
  const { data, error } = await supabase
    .from("run_criterion_responses")
    .update(patch)
    .eq("id", id)
    .select()
    .single();
  return unwrap(data, error);
}

export async function listEvidence(runId: string): Promise<EvidenceReference[]> {
  const { data, error } = await supabase
    .from("evidence_references")
    .select("*")
    .eq("run_id", runId)
    .order("added_at");
  return unwrap(data, error);
}

export async function createEvidence(input: {
  organisation_id: string;
  run_id: string;
  evidence_type: string;
  label: string;
  reference_kind: EvidenceKind;
  locator_original: string | null;
}): Promise<EvidenceReference> {
  const { data, error } = await supabase
    .from("evidence_references")
    .insert(input)
    .select()
    .single();
  return unwrap(data, error);
}

export async function listEvidenceLinks(orgId: string): Promise<ResponseEvidenceLink[]> {
  const { data, error } = await supabase
    .from("response_evidence_links")
    .select("*")
    .eq("organisation_id", orgId);
  return unwrap(data, error);
}

export async function linkEvidence(
  orgId: string,
  evidenceId: string,
  criterionResponseId: string,
): Promise<void> {
  const { error } = await supabase.from("response_evidence_links").insert({
    organisation_id: orgId,
    evidence_reference_id: evidenceId,
    criterion_response_id: criterionResponseId,
  });
  if (error) throw new Error(error.message);
}

/**
 * Withdraw one response-to-evidence association (0009). The evidence row is
 * untouched; the database resolves tenant and editability internally, and a
 * non-member cannot distinguish "exists elsewhere" from "does not exist".
 */
export async function unlinkEvidence(
  responseId: string,
  evidenceId: string,
): Promise<void> {
  const { error } = await supabase.rpc("assessment_unlink_evidence", {
    p_response_id: responseId,
    p_evidence_id: evidenceId,
  });
  if (error) throw new Error(error.message);
}

/** The exact input evaluate() will receive - gate 10's reference object. */
export async function runInput(runId: string): Promise<unknown> {
  const { data, error } = await supabase.rpc("assessment_run_input", {
    p_run_id: runId,
  });
  if (error) throw new Error(error.message);
  return data;
}

// ---- Evaluation results (session 5) ------------------------------------
// The console READS evaluations and INVOKES the Edge Function; it computes
// nothing. "The kernel calculates; the console renders."

/**
 * Every evaluation of a run. Selection rules live in ONE module —
 * lib/evaluationEligibility.ts (F4.6 Amendment 2) — so the results screen,
 * the report and the comparison cannot drift on which row represents a run.
 */
export async function listEvaluations(runId: string): Promise<EvaluationRow[]> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("*")
    .eq("run_id", runId)
    .order("seq", { ascending: false });
  return unwrap(data, error);
}

/**
 * The most recent evaluation for a run, or null when none exists yet - the
 * not-yet-evaluated state is a real state and must render as one (gate 5).
 *
 * Ordered by `seq`, the monotonic identity column - NEVER by `evaluated_at`,
 * which is the transaction timestamp and ties arbitrarily for rows written
 * together (0004 §3 records the incident). The ordering rule itself lives in
 * the shared eligibility module.
 */
export async function latestEvaluation(runId: string): Promise<EvaluationRow | null> {
  return selectLatestEvaluation(await listEvaluations(runId));
}

/** The latest COMPARISON-ELIGIBLE evaluation (F4.6 Amendment 2): the shared
 *  predicate applied to the same rows. */
export async function eligibleEvaluation(runId: string): Promise<EvaluationRow | null> {
  return selectEligibleEvaluation(await listEvaluations(runId));
}

/**
 * Invoke the authoritative evaluation (gate 1). The request is `{ run_id }`
 * and nothing else - no tenant, no actor, no scores; the Edge Function
 * derives identity from the JWT and the database refuses a caller without
 * write authority (0004 §6), so this button can be shown optimistically and
 * the boundary still holds.
 *
 * `publish` is deliberately not a parameter: session 5 is display-only, and
 * a function that COULD ask for publication would fail gate 10 in spirit.
 */
// ---- Findings & actions register (F4.1–F4.4) ----------------------------
// The register is the durable managed record; the kernel snapshot stays the
// immutable source. Every mutation below is an assessment_* function — the
// database resolves tenant and actor internally, refuses cross-tenant
// probes nonexistence-equivalently, and the UI mirrors it without ever
// being the boundary.

export async function listFindings(runId: string): Promise<FindingRow[]> {
  const { data, error } = await supabase
    .from("findings")
    .select("*")
    .eq("run_id", runId)
    .order("seq", { ascending: false });
  return unwrap(data, error);
}

export async function transitionFinding(
  findingId: string,
  toState: FindingStatus,
): Promise<void> {
  const { error } = await supabase.rpc("assessment_transition_finding", {
    p_finding_id: findingId,
    p_to_state: toState,
  });
  if (error) throw new Error(error.message);
}

/** Kernel-promotion provenance for one evaluation — which kernel findings
 *  already have a managed expression, and which finding rows carry a
 *  kernel-origin badge. */
export async function listKernelOrigins(
  evaluationId: string,
): Promise<FindingKernelOrigin[]> {
  const { data, error } = await supabase
    .from("finding_kernel_origins")
    .select("*")
    .eq("evaluation_id", evaluationId);
  return unwrap(data, error);
}

export async function listOriginsForFindings(
  findingIds: string[],
): Promise<FindingKernelOrigin[]> {
  if (findingIds.length === 0) return [];
  const { data, error } = await supabase
    .from("finding_kernel_origins")
    .select("*")
    .in("finding_id", findingIds);
  return unwrap(data, error);
}

/**
 * Promote one kernel finding into the managed register (0013). The caller
 * supplies ONLY the key and the managed expression — every provenance value
 * derives from the immutable snapshot server-side, and the database demands
 * a rationale when the wording or severity differs from the original or the
 * source evaluation is stale. The UI surfaces that refusal; it never
 * pre-computes it.
 */
export async function promoteKernelFinding(input: {
  evaluationId: string;
  kernelFindingKey: string;
  title: string;
  managedDescription?: string | null;
  severity?: FindingSeverity | null;
  rationale?: string | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc("assessment_promote_kernel_finding", {
    p_evaluation_id: input.evaluationId,
    p_kernel_finding_key: input.kernelFindingKey,
    p_title: input.title,
    p_managed_description: input.managedDescription ?? null,
    p_severity: input.severity ?? null,
    p_rationale: input.rationale ?? null,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function listActions(orgId: string): Promise<ActionRow[]> {
  const { data, error } = await supabase
    .from("actions")
    .select("*")
    .eq("organisation_id", orgId)
    .order("seq", { ascending: false });
  return unwrap(data, error);
}

/** ALL link occurrences for the tenant, removed ones included — the link
 *  table is the authoritative relationship history (0012), and the register
 *  renders current scope from active rows and past scope from intervals. */
export async function listActionLinks(orgId: string): Promise<ActionFindingLink[]> {
  const { data, error } = await supabase
    .from("action_finding_links")
    .select("*")
    .eq("organisation_id", orgId)
    .order("created_at");
  return unwrap(data, error);
}

/** The narrative history for one action — reasons and actors ride here;
 *  the typed state lives in the link table itself. */
export async function listActionChanges(actionId: string): Promise<ActionChange[]> {
  const { data, error } = await supabase
    .from("action_changes")
    .select("*")
    .eq("action_id", actionId)
    .order("seq");
  return unwrap(data, error);
}

export async function createAction(input: {
  title: string;
  priority: FindingSeverity;
  ownerId: string;
  findingIds: string[];
  requiredOutcome?: string | null;
  dueDate?: string | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc("assessment_create_action", {
    p_title: input.title,
    p_priority: input.priority,
    p_owner_id: input.ownerId,
    p_finding_ids: input.findingIds,
    p_required_outcome: input.requiredOutcome ?? null,
    p_due_date: input.dueDate ?? null,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function transitionAction(
  actionId: string,
  toState: ActionStatus,
  reason?: string | null,
): Promise<void> {
  const { error } = await supabase.rpc("assessment_transition_action", {
    p_action_id: actionId,
    p_to_state: toState,
    p_reason: reason ?? null,
  });
  if (error) throw new Error(error.message);
}

/** Attach a finding to an OPEN action (0012). The database refuses withdrawn
 *  findings, requires a reason for a REATTACHMENT (a removed occurrence for
 *  the pair exists), and refuses a duplicate active link loudly. */
export async function attachActionFinding(
  actionId: string,
  findingId: string,
  reason?: string | null,
): Promise<string> {
  const { data, error } = await supabase.rpc("assessment_attach_action_finding", {
    p_action_id: actionId,
    p_finding_id: findingId,
    p_reason: reason ?? null,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

/** Remove one link OCCURRENCE by its id, never by the pair — a stale id
 *  cannot touch a later reattachment. Reason mandatory and structural. */
export async function unlinkActionFinding(
  linkId: string,
  reason: string,
): Promise<void> {
  const { error } = await supabase.rpc("assessment_unlink_action_finding", {
    p_link_id: linkId,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

export async function invokeEvaluation(
  runId: string,
  opts: { publish?: boolean } = {},
): Promise<void> {
  // `publish: true` asks evaluate-run to also transition the run to
  // `finalized` (its step 7), refused separately if the caller lacks the
  // authority. Existing callers pass nothing and are unchanged.
  const { error } = await supabase.functions.invoke("evaluate-run", {
    body: opts.publish ? { run_id: runId, publish: true } : { run_id: runId },
  });
  if (error) {
    // FunctionsHttpError carries the response; the function's machine-readable
    // failure body beats supabase-js's generic prose when it can be read.
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === "function") {
      try {
        const body = (await ctx.json()) as { error?: string; message?: string };
        throw new Error(body.message ?? body.error ?? error.message);
      } catch (e) {
        if (e instanceof Error && e.message !== error.message) throw e;
      }
    }
    throw new Error(error.message);
  }
}

// ---- Reassessment baseline (F4.6, 0015) ---------------------------------
// The binding is a recorded fact with derived attribution; the comparison is
// computed at render from both sides' recorded identities and stored nowhere.

/** The reassessment-baseline binding of a run, or null when none is set. */
export async function getRunBaseline(runId: string): Promise<RunBaseline | null> {
  const { data, error } = await supabase
    .from("run_baselines")
    .select("*")
    .eq("run_id", runId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data as RunBaseline | null;
}

/**
 * The runs eligible as a baseline for this one, mirroring the database's
 * answer so the picker rarely offers what the server refuses: same entity,
 * finalized or published, and created strictly BEFORE the reassessing run
 * (Amendment 1 — run facts, never evaluation timestamps). The database
 * remains the boundary; this is display courtesy.
 */
export async function listEligibleBaselines(run: Run): Promise<Run[]> {
  const { data, error } = await supabase
    .from("runs")
    .select("*")
    .eq("entity_id", run.entity_id)
    .in("state", ["finalized", "published"])
    .lt("created_at", run.created_at)
    .neq("id", run.id)
    .order("created_at", { ascending: false });
  return unwrap(data, error);
}

/** Bind (or atomically rebind) the baseline. Refusals — wrong window, wrong
 *  state, precedence, authority — surface verbatim from the database. */
export async function setRunBaseline(
  runId: string,
  baselineRunId: string,
): Promise<string> {
  const { data, error } = await supabase.rpc("assessment_set_run_baseline", {
    p_run_id: runId,
    p_baseline_run_id: baselineRunId,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function clearRunBaseline(runId: string): Promise<void> {
  const { error } = await supabase.rpc("assessment_clear_run_baseline", {
    p_run_id: runId,
  });
  if (error) throw new Error(error.message);
}

// ---- Respondent (PMO Self-Assessment MVP, DESIGN §4.2–§4.5) --------------
// The respondent's whole data surface. Everything goes through the same
// tables, RPCs and RLS as the consultant console; nothing here is a new
// privilege. What is new is only which rows the respondent view asks for.

/**
 * Runs assigned to the signed-in user, each with whether its PINNED
 * definition is self-serve. Read from `instrument_versions.definition`, the
 * frozen snapshot — the same fact 0019's lock reads, so the view and the
 * database cannot disagree about which runs are self-assessments.
 */
export async function listMyAssignedRuns(): Promise<AssignedRun[]> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError) throw new Error(userError.message);
  const uid = userData.user?.id;
  if (!uid) throw new Error("no authenticated user");

  const { data: asg, error: asgError } = await supabase
    .from("run_assignments")
    .select("run_id")
    .eq("assessor_id", uid);
  if (asgError) throw new Error(asgError.message);
  const runIds = (asg ?? []).map((a) => a.run_id as string);
  if (runIds.length === 0) return [];

  const { data: runs, error: runError } = await supabase
    .from("runs")
    .select("id, organisation_id, state, instrument_version_id")
    .in("id", runIds);
  if (runError) throw new Error(runError.message);
  const versionIds = [...new Set((runs ?? []).map((r) => r.instrument_version_id as string))];
  if (versionIds.length === 0) return [];

  const { data: versions, error: verError } = await supabase
    .from("instrument_versions")
    .select("id, self_serve:definition->instrument->>self_serve_eligible")
    .in("id", versionIds);
  if (verError) throw new Error(verError.message);
  const selfServe = new Map(
    (versions ?? []).map((v) => [v.id as string, (v as { self_serve: string | null }).self_serve === "true"]),
  );

  return (runs ?? []).map((r) => ({
    run_id: r.id as string,
    organisation_id: r.organisation_id as string,
    state: r.state as string,
    instrument_version_id: r.instrument_version_id as string,
    self_serve: selfServe.get(r.instrument_version_id as string) ?? false,
  }));
}

/** What the report names: the instrument's title, version and identity. */
export async function getVersionIdentity(
  versionId: string,
): Promise<{ version: string; definition_hash: string; title: string; slug: string }> {
  const { data, error } = await supabase
    .from("instrument_versions")
    .select("version, definition_hash, instruments (title, slug)")
    .eq("id", versionId)
    .single();
  if (error) throw new Error(error.message);
  type Inst = { title: string; slug: string };
  const raw = (data as { instruments: Inst | Inst[] | null }).instruments;
  const inst = Array.isArray(raw) ? raw[0] : raw;
  return {
    version: data.version as string,
    definition_hash: data.definition_hash as string,
    title: inst?.title ?? "",
    slug: inst?.slug ?? "",
  };
}

/**
 * Submission (decision §6 step 5): the run moves to review, is evaluated,
 * and is finalized — all under the respondent's own `write` authority, all
 * refused by the database if anything is not in order (an unanswered
 * question fails qualification and finalization is refused). After it,
 * 0019 refuses the respondent's way back.
 */
export async function submitSelfAssessment(runId: string, state: RunState): Promise<void> {
  if (state === "draft") await transitionRun(runId, "ready_for_review");
  await invokeEvaluation(runId, { publish: true });
}
